import React, { Suspense, lazy, useEffect } from 'react';
// import StyleGuide from './pages/other/StyleGuide/StyleGuide';
// import BlogArticleTemplate from './pages/blog/BlogArticleTemplate/BlogArticleTemplate';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { UserProvider } from './contexts/UserContext/UserContext';
import { PrivateRoute } from './components/account/PrivateRoute/PrivateRoute';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as UrqlProvider } from 'urql';
import { client } from './graphql/gateway-client';
import './css/sitewide.module.scss';
import { CookieProvider } from './contexts/CookieContext/CookieContext';
import {
    PAYMENT_CANCEL_REDIRECT_PATH,
    PAYMENT_METHOD_UPDATE_CANCEL_REDIRECT_URL,
    PAYMENT_METHOD_UPDATE_SUCCESS_REDIRECT_URL,
    PAYMENT_SUCCESS_REDIRECT_PATH,
} from './utils/global-variables';
import { Hooks } from './components/default/Hooks/Hooks';
// import NewAccountCreated from './pages/account/public/NewAccountCreated/NewAccountCreated';
import { Loading } from './components/default/States/States';
import FreeCredits from './pages/other/FreeCredits';
import tracker from './utils/HelperFunctions/openReplay';
import { initGA, logPageView } from './utils/HelperFunctions/googleAnalytics/google-analytics';
import RouteScroller from 'components/default/RouteScroller';

const Homepage = lazy(() => import('./pages/other/Homepage/Homepage'));
const Login = lazy(() => import('./pages/account/public/Login/Login'));
const Terms = lazy(() => import('./pages/legal/Terms/Terms'));
const Privacy = lazy(() => import('./pages/legal/Privacy/Privacy'));
const CreateAccount = lazy(() => import('./pages/account/public/CreateAccount/CreateAccount'));
const EditAccount = lazy(() => import('./pages/account/private/EditAccount/EditAccount'));
const NewPassword = lazy(() => import('./pages/account/public/NewPassword/NewPassword'));
const NewPasswordSuccess = lazy(
    () => import('./pages/account/public/NewPasswordSuccess/NewPasswordSuccess')
);
const ResetPassword = lazy(() => import('./pages/account/public/ResetPassword/ResetPassword'));
const About = lazy(() => import('./pages/other/About/About'));
const Pricing = lazy(() => import('./pages/other/Pricing/Pricing'));
const Dashboard = lazy(() => import('./pages/account/private/Dashboard/Dashboard'));
const Websites = lazy(() => import('./pages/account/private/Websites/Websites'));
const Tests = lazy(() => import('./pages/account/private/Tests/Tests'));
const Test = lazy(() => import('./pages/account/private/Tests/Test/Test'));
const CreateTest = lazy(() => import('./pages/account/private/Tests/CreateTest/CreateTest'));
const CreateTestSuccess = lazy(
    () => import('./pages/account/private/Tests/CreateTest/CreateTestSuccess/CreateTestSuccess')
);
const UserProfile = lazy(() => import('./pages/account/private/UserProfile/UserProfile'));
const PaymentSuccessful = lazy(
    () => import('./pages/account/private/payments/PaymentSuccessful/PaymentSuccessful')
);
const PaymentCancelled = lazy(
    () => import('./pages/account/private/payments/PaymentCancelled/PaymentCancelled')
);
const NotFound = lazy(() => import('./pages/other/errors/404/404'));
const AffiliateProgram = lazy(() => import('./pages/affiliates/AffiliateProgram/AffiliateProgram'));
const AffiliateDashboard = lazy(
    () => import('./pages/account/private/affiliate/AffiliateDashboard/AffiliateDashboard')
);
const AffiliateRegistry = lazy(
    () => import('./pages/affiliates/AffiliateRegistry/AffiliateRegistry')
);
const AffiliateLogin = lazy(() => import('./pages/affiliates/AffiliateLogin/AffiliateLogin'));
const ReferralListPage = lazy(
    () => import('./pages/account/private/affiliate/ReferralListPage/ReferralListPage')
);
const AffiliatePayments = lazy(
    () => import('./pages/account/private/affiliate/AffiliatePayments/AffiliatePayments')
);
const AffiliateBalance = lazy(
    () => import('./pages/account/private/affiliate/AffiliateBalance/AffiliateBalance')
);
const AffiliateCommissions = lazy(
    () => import('./pages/account/private/affiliate/AffiliateCommissions/AffiliateCommissions')
);
const AffiliateFaq = lazy(() => import('./pages/affiliates/AffiliateFaq/AffiliateFaq'));
const AffiliateSessions = lazy(
    () => import('./pages/account/private/affiliate/AffiliateSessions/AffiliateSessions')
);
const Cookies = lazy(() => import('./pages/legal/Cookies/Cookies'));
const Pages = lazy(() => import('./pages/account/private/Pages/Pages'));
const TestResults = lazy(
    () => import('./pages/account/private/Tests/Test/TestResults/TestResults')
);
const TestVariant = lazy(
    () => import('./pages/account/private/Tests/Test/TestVariant/TestVariant')
);
const ScriptSetup = lazy(() => import('./pages/support/ScriptSetup/ScriptSetup'));
const Support = lazy(() => import('./pages/support/Support'));
const ScriptVerification = lazy(
    () => import('./pages/support/ScriptVerification/ScriptVerification')
);
const Contact = lazy(() => import('./pages/other/Contact/Contact'));
const QuickTest = lazy(() => import('./pages/other/QuickTest'));
const PaymentMethodUpdateCancelled = lazy(
    () =>
        import(
            './pages/account/private/payments/PaymentMethodUpdateCancelled/PaymentMethodUpdateCancelled'
        )
);
const PaymentMethodUpdateSuccess = lazy(
    () =>
        import(
            './pages/account/private/payments/PaymentMethodUpdateSuccess/PaymentMethodUpdateSuccess'
        )
);
const Goals = lazy(() => import('./pages/account/private/UserProfile/Avatar/Goals/Goals'));
const Business = lazy(() => import('./pages/account/private/UserProfile/Avatar/Business/Business'));
const Demographics = lazy(
    () => import('./pages/account/private/UserProfile/Avatar/Demographics/Demographics')
);
const PreviousExperience = lazy(
    () => import('./pages/account/private/UserProfile/Avatar/PreviousExperience/PreviousExperience')
);
const AvatarSuccess = lazy(
    () => import('./pages/account/private/UserProfile/Avatar/AvatarSuccess/AvatarSuccess')
);
const TestCreationGuide = lazy(
    () => import('./pages/support/Testing/TestCreationGuide/TestCreationGuide')
);
const ProductImageEffectiveness = lazy(
    () =>
        import(
            './pages/support/Testing/Examples/ProductImageEffectiveness/ProductImageEffectiveness'
        )
);
const FormTestCreation = lazy(
    () => import('./pages/support/Testing/Examples/FormTestCreation/FormTestCreation')
);
const NewNewsletterSubscriber = lazy(
    () => import('./pages/subscribers/NewNewsletterSubscriber/NewNewsletterSubscriber')
);
const Partnerships = lazy(() => import('./pages/affiliates/Partnerships/Partnerships'));
const AbTestingHeadlines = lazy(() => import('./pages/blog/AbTestingHeadlines/AbTestingHeadlines'));
const Blog = lazy(() => import('./pages/blog/Blog'));
const AbTestingMultipleVariants = lazy(
    () => import('./pages/blog/AbTestingMultipleVariants/AbTestingMultipleVariants')
);
const AbTestingToolFree = lazy(() => import('./pages/blog/AbTestingToolFree/AbTestingToolFree'));
const ImproveFormConversions = lazy(
    () => import('./pages/blog/ImproveFormConversions/ImproveFormConversions')
);
const ImproveProductPageConversions = lazy(
    () => import('./pages/blog/ImproveProductPageConversions/ImproveProductPageConversions')
);
const WordPressAndWooCommerceAbTesting = lazy(
    () => import('./pages/blog/WordPressAndWooCommerceAbTesting/WordPressAndWooCommerceAbTesting')
);
const QualifiedLeadSuccess = lazy(
    () => import('./pages/account/private/successes/QualifiedLeadSuccess')
);
const StandardLeadSuccess = lazy(
    () => import('./pages/account/private/successes/StandardLeadSuccess')
);
const Services = lazy(() => import('./pages/other/Services/Services'));
const MainLeadMagnetPage = lazy(() => import('./pages/landing/non-paid/MainLeadMagnetPage'));
// const FacebookAdOne = lazy(() => import('./pages/landing/paid-ads/FacebookAdOne'));
const FacebookAdTwo = lazy(() => import('./pages/landing/paid-ads/FacebookAdTwo'));
const FacebookAdThree = lazy(() => import('./pages/landing/paid-ads/FacebookAdThree'));
const FacebookAdFour = lazy(() => import('./pages/landing/paid-ads/FacebookAdFour'));
const FacebookAdFive = lazy(() => import('./pages/landing/paid-ads/FacebookAdFive'));
const FacebookAdSix = lazy(() => import('./pages/landing/paid-ads/FacebookAdSix'));
const FacebookAdSeven = lazy(() => import('./pages/landing/paid-ads/FacebookAdSeven'));
const FacebookAdEight = lazy(() => import('./pages/landing/paid-ads/FacebookAdEight'));
const FacebookAdNine = lazy(() => import('./pages/landing/paid-ads/FacebookAdNine'));
const FacebookAdTen = lazy(() => import('./pages/landing/paid-ads/FacebookAdTen'));
const FacebookAdEleven = lazy(() => import('./pages/landing/paid-ads/FacebookAdEleven'));
const FacebookAdTwelve = lazy(() => import('./pages/landing/paid-ads/FacebookAdTwelve'));
const FacebookAdAiOne = lazy(() => import('./pages/landing/paid-ads/FacebookAdAiOne'));
const ShopifyAdOne = lazy(() => import('./pages/landing/paid-ads/ShopifyAdOne'));
const HeadlinesLandingPage = lazy(() => import('./pages/landing/non-paid/HeadlinesLandingPage'));
const AffiliateTerms = lazy(() => import('./pages/legal/AffiliateTerms'));
const DPA = lazy(() => import('./pages/legal/DPA/DPA'));
const LIA = lazy(() => import('./pages/legal/LIA/LIA'));
const LegalGuidance = lazy(() => import('./pages/legal/LegalGuidance/LegalGuidance'));
const CreateHeadlineTest = lazy(() => import('./pages/landing/non-paid/CreateHeadlineTest'));
const AbTestSuggester = lazy(() => import('./pages/tools/ab-test-suggester/AbTestSuggester'));

const AppContent = () => {
    const location = useLocation();

    useEffect(() => {
        if (process.env.NODE_ENV === 'production') {
            tracker.start();
            logPageView(location.pathname + location.search);
        }
    }, [location]);

    return (
        <>
            <RouteScroller />
            <Suspense fallback={<Loading entirePage={true} centralised={true} />}>
                <Routes>
                    <Route path='/' element={<Homepage />} />
                    {/* <Route path='/styles' element={<StyleGuide />} /> */}
                    {/* <Route path='/blog/template' element={<BlogArticleTemplate />} /> */}
                    <Route path='/tools/ab-test-suggester' element={<AbTestSuggester />} />
                    <Route path='/blog/ab-testing-headlines' element={<AbTestingHeadlines />} />
                    <Route
                        path='/blog/ab-testing-multiple-variants'
                        element={<AbTestingMultipleVariants />}
                    />
                    <Route path='/blog/free-ab-testing-tool' element={<AbTestingToolFree />} />
                    <Route
                        path='/blog/improve-form-conversions-with-ab-testing'
                        element={<ImproveFormConversions />}
                    />
                    <Route
                        path='/blog/improve-product-page-conversions-with-ab-testing'
                        element={<ImproveProductPageConversions />}
                    />
                    <Route
                        path='/blog/wordpress-and-woocommerce-pluginless-ab-testing'
                        element={<WordPressAndWooCommerceAbTesting />}
                    />
                    <Route path='/about' element={<About />} />
                    <Route path='/free-topup' element={<FreeCredits />} />
                    <Route path='/blog' element={<Blog />} />
                    <Route path='/pricing' element={<Pricing />} />
                    <Route path='/services' element={<Services />} />
                    <Route path='/login' element={<Login />} />
                    {/* <Route path='/new-account-created' element={<NewAccountCreated />} /> superseded */}
                    <Route path='/contact' element={<Contact />} />
                    <Route path='/create-test-from-suggestion' element={<QuickTest />} />
                    <Route path='/create-account' element={<CreateAccount />} />
                    <Route path='/new-password' element={<NewPassword />} />
                    <Route path='/new-password-success' element={<NewPasswordSuccess />} />
                    <Route path='/reset-password' element={<ResetPassword />} />
                    <Route
                        path='/user'
                        element={
                            <PrivateRoute>
                                <Dashboard />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        // keep path as-is for now at least, in case changing this would screw with any tracking
                        path='/user/new-account-success'
                        element={
                            <PrivateRoute>
                                <QualifiedLeadSuccess />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/welcome-new-user'
                        element={
                            <PrivateRoute>
                                <StandardLeadSuccess />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/websites'
                        element={
                            <PrivateRoute>
                                <Websites />
                            </PrivateRoute>
                        }
                    >
                        <Route
                            path='/user/websites/page/:currentPage'
                            element={
                                <PrivateRoute>
                                    <Websites />
                                </PrivateRoute>
                            }
                        />
                    </Route>
                    <Route
                        path='/user/tests'
                        element={
                            <PrivateRoute>
                                <Tests />
                            </PrivateRoute>
                        }
                    >
                        <Route
                            path='/user/tests/page/:currentPage'
                            element={
                                <PrivateRoute>
                                    <Tests />
                                </PrivateRoute>
                            }
                        />
                    </Route>
                    <Route
                        path='/user/tests/:testId'
                        element={
                            <PrivateRoute>
                                <Test />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/tests/:testId/results'
                        element={
                            <PrivateRoute>
                                <TestResults />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/tests/:testId/variant/:variantId'
                        element={
                            <PrivateRoute>
                                <TestVariant />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/tests/new'
                        element={
                            <PrivateRoute>
                                <CreateTest />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/tests/new/success/:testId'
                        element={
                            <PrivateRoute>
                                <CreateTestSuccess />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/pages'
                        element={
                            <PrivateRoute>
                                <Pages />
                            </PrivateRoute>
                        }
                    >
                        <Route
                            path='/user/pages/page/:currentPage'
                            element={
                                <PrivateRoute>
                                    <Pages />
                                </PrivateRoute>
                            }
                        />
                    </Route>
                    <Route path={PAYMENT_CANCEL_REDIRECT_PATH} element={<PaymentCancelled />} />
                    <Route path={PAYMENT_SUCCESS_REDIRECT_PATH} element={<PaymentSuccessful />} />
                    <Route
                        path={PAYMENT_METHOD_UPDATE_SUCCESS_REDIRECT_URL}
                        element={<PaymentMethodUpdateSuccess />}
                    />
                    <Route
                        path={PAYMENT_METHOD_UPDATE_CANCEL_REDIRECT_URL}
                        element={<PaymentMethodUpdateCancelled />}
                    />
                    <Route
                        path='/user/profile'
                        element={
                            <PrivateRoute>
                                <UserProfile />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/profile/edit'
                        element={
                            <PrivateRoute>
                                <Goals />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/profile/edit/2'
                        element={
                            <PrivateRoute>
                                <Business />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/profile/edit/3'
                        element={
                            <PrivateRoute>
                                <Demographics />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/profile/edit/4'
                        element={
                            <PrivateRoute>
                                <PreviousExperience />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/profile/edit/success'
                        element={
                            <PrivateRoute>
                                <AvatarSuccess />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/profile/affiliate'
                        element={
                            <PrivateRoute>
                                <AffiliateDashboard />
                            </PrivateRoute>
                        }
                    />
                    <Route
                        path='/user/profile/affiliate/referral-list'
                        element={
                            <PrivateRoute>
                                <ReferralListPage />
                            </PrivateRoute>
                        }
                    >
                        <Route
                            path='/user/profile/affiliate/referral-list/page/:currentPage'
                            element={
                                <PrivateRoute>
                                    <ReferralListPage />
                                </PrivateRoute>
                            }
                        />
                    </Route>
                    <Route
                        path='/user/profile/affiliate/payments'
                        element={
                            <PrivateRoute>
                                <AffiliatePayments />
                            </PrivateRoute>
                        }
                    >
                        <Route
                            path='/user/profile/affiliate/payments/page/:currentPage'
                            element={
                                <PrivateRoute>
                                    <AffiliatePayments />
                                </PrivateRoute>
                            }
                        />
                    </Route>
                    <Route
                        path='/user/profile/affiliate/commissions'
                        element={
                            <PrivateRoute>
                                <AffiliateCommissions />
                            </PrivateRoute>
                        }
                    >
                        <Route
                            path='/user/profile/affiliate/commissions/page/:currentPage'
                            element={
                                <PrivateRoute>
                                    <AffiliateCommissions />
                                </PrivateRoute>
                            }
                        />
                    </Route>
                    <Route
                        path='/user/profile/affiliate/sessions'
                        element={
                            <PrivateRoute>
                                <AffiliateSessions />
                            </PrivateRoute>
                        }
                    >
                        <Route
                            path='/user/profile/affiliate/sessions/page/:currentPage'
                            element={
                                <PrivateRoute>
                                    <AffiliateSessions />
                                </PrivateRoute>
                            }
                        />
                    </Route>
                    <Route
                        path='/user/profile/affiliate/balance'
                        element={
                            <PrivateRoute>
                                <AffiliateBalance />
                            </PrivateRoute>
                        }
                    >
                        <Route
                            path='/user/profile/affiliate/balance/page/:currentPage'
                            element={
                                <PrivateRoute>
                                    <AffiliateBalance />
                                </PrivateRoute>
                            }
                        />
                    </Route>
                    <Route
                        path='/user/edit-account'
                        element={
                            <PrivateRoute>
                                <EditAccount />
                            </PrivateRoute>
                        }
                    />
                    <Route path='/support' element={<Support />} />
                    <Route path='/support/script-setup' element={<ScriptSetup />} />
                    <Route path='/support/script-verification' element={<ScriptVerification />} />
                    <Route path='/support/test-creation/guide' element={<TestCreationGuide />} />
                    <Route
                        path='/support/test-examples/images'
                        element={<ProductImageEffectiveness />}
                    />
                    <Route path='/support/test-examples/forms' element={<FormTestCreation />} />
                    <Route path='/terms' element={<Terms />} />
                    <Route path='/affiliate-terms' element={<AffiliateTerms />} />
                    <Route path='/privacy' element={<Privacy />} />
                    <Route path='/data-processing-agreement' element={<DPA />} />
                    <Route path='/legitimate-interests-assessment' element={<LIA />} />
                    <Route path='/legal-guidance' element={<LegalGuidance />} />
                    <Route path='/cookie-policy' element={<Cookies />} />
                    <Route path='/affiliates' element={<AffiliateProgram />} />
                    <Route path='/partnerships' element={<Partnerships />} />
                    <Route path='/affiliates/register' element={<AffiliateRegistry />} />
                    <Route path='/affiliates/login' element={<AffiliateLogin />} />
                    <Route path='/affiliates/faq' element={<AffiliateFaq />} />
                    <Route path='/quickwins' element={<MainLeadMagnetPage />} />
                    <Route
                        path='/newsletter-signup/success'
                        element={<NewNewsletterSubscriber />}
                    />
                    {/* <Route path='/free-headline-ab-test' element={<FacebookAdOne />} /> */}
                    <Route path='/payg-based-ab-testing' element={<FacebookAdTwo />} />
                    <Route path='/payg-based-ab-testing-v2' element={<FacebookAdThree />} />
                    <Route path='/payg-based-ab-testing-v3' element={<FacebookAdFour />} />
                    <Route path='/payg-based-ab-testing-v4' element={<FacebookAdFive />} />
                    <Route path='/payg-based-ab-testing-v6' element={<FacebookAdSix />} />
                    <Route path='/payg-based-ab-testing-v7' element={<FacebookAdSeven />} />
                    <Route path='/payg-based-ab-testing-v8' element={<FacebookAdEight />} />
                    <Route path='/payg-based-ab-testing-v9' element={<FacebookAdNine />} />
                    <Route path='/payg-based-ab-testing-v10' element={<FacebookAdTen />} />
                    <Route path='/payg-based-ab-testing-v11' element={<FacebookAdEleven />} />
                    <Route path='/payg-based-ab-testing-v12' element={<FacebookAdTwelve />} />
                    <Route path='/free-automated-cro-ideas-v1' element={<FacebookAdAiOne />} />
                    <Route path='/shopify-cro-ideas-v1' element={<ShopifyAdOne />} />
                    <Route path='/headline' element={<HeadlinesLandingPage />} />
                    <Route path='/create-headline-tests' element={<CreateHeadlineTest />} />
                    <Route path='*' element={<NotFound />} />
                </Routes>
            </Suspense>
        </>
    );
};

const App = () => {
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') {
            initGA(); // only once in production
        }
    }, []);

    return (
        <HelmetProvider>
            <UrqlProvider value={client}>
                <CookieProvider>
                    <Hooks />
                    <UserProvider>
                        <BrowserRouter>
                            <AppContent />
                        </BrowserRouter>
                    </UserProvider>
                </CookieProvider>
            </UrqlProvider>
        </HelmetProvider>
    );
};

export default App;
